'use client'

import Tooltip from '@component/tooltip'
import IconLogo from '@icon/const/logo.svg'
import getRouter, { URLS } from '@router'
import { global } from '@store/index'
import { toNumber, toPrice } from '@utils'
import classNames from 'classnames'
import { get, replace, toLower } from 'lodash-es'
import DisablePrefetchLink from '@component/disablePrefetchLink'
import { useRecoilValue } from 'recoil'

export default function Avm({
  data
}) {

  const avmData = get(data, 'avm')
  const suburbSaleRVDiff = toNumber(get(avmData, 'suburbSaleRvdiff', 0))
  const avm = toNumber(replace(get(avmData, 'avm'), /[^.\d]+/g, ''))
  const rv = toNumber(replace(get(avmData, 'rv'), /[^.\d]+/g, ''))
  const avmCommentOption = get(data, 'avmCommentOption', 0)
  const confidenceScore = toLower(get(avmData, 'confidenceScore', ''))

  return (
    <div>
      <div className='pt-110 pb-80 md:pt-80 md:pb-100'>
        <div className='relative'>
          <div className='h-2' style={{
            background: 'linear-gradient(90deg, rgba(0,0,0,0.03), black, rgba(0,0,0,0.03))'
          }}></div>
          <AvmItem name="Low" price={avmData.low} left />
          {avmData.avmRvRate && avmData.rv && avmData.avm !== avmData.rv && <AvmItem name='RV*' price={avmData.rv} leftHalf={avm > rv} rightHalf={avm < rv}></AvmItem>}
          <AvmItem name="High" price={avmData.high} right />
          <AvmItem avm={avmData.avm} middle className='hidden md:block'>
            <div className='text-base flex items-center justify-center gap-x-4'>
              <IconLogo className='w-14' />
              OneRoof Estimate
            </div>
            <AgentComment option={avmCommentOption} confidenceScore={confidenceScore} />
          </AvmItem>
          <AvmItem middle className='md:hidden'>
            <div className='pb-30 absolute bottom-10 left-0 -translate-x-1/2 whitespace-nowrap'>
              <div className='text-base flex items-center justify-center gap-x-4 -mb-4'>
                <IconLogo className='w-14' />
                OneRoof Estimate
              </div>
              <AgentComment option={avmCommentOption} confidenceScore={confidenceScore} />
              <div className='text-3xl font-bold text-secondary'>{toPrice(avmData.avm)}</div>
              <i className='w-2 h-20 absolute bottom-6 left-1/2 -translate-x-1/2 bg-font' />
            </div>
          </AvmItem>
        </div>
      </div>
      <div className='flex flex-col-reverse md:flex-row md:items-center md:justify-around'>
        <span className='md:max-w-270 text-sm pt-20 px-20 md:p-0'>
          This is an automated online valuation. <DisablePrefetchLink prefetch={false} href={getRouter(URLS.propertyValuations)} className='text-primary underline' target='_blank'>Click here</DisablePrefetchLink> for more information.
        </span>
        <span className='md:max-w-300 border rounded-md text-sm px-14 py-12'>
          Sales in this suburb over the last 12 months have averaged
          <span className={classNames('ml-4', suburbSaleRVDiff > 0 ? 'text-greenrgb' : 'text-[#FF0101]')}>
            {Math.abs(suburbSaleRVDiff)}% {suburbSaleRVDiff > 0 ? 'above RV' : 'below RV'}
          </span>
        </span>
      </div>
    </div>
  )
}


function AvmItem({
  price,
  name,
  avm,
  left = false,
  right = false,
  leftHalf = false,
  rightHalf = false,
  middle = false,
  className,
  children
}) {
  return (
    <div className={classNames(
      'text-center font-medium absolute top-0 pt-10',
      { 'left-0': left },
      { 'right-0': right },
      { 'left-1/4 md:-translate-x-1/2': leftHalf },
      { 'right-1/4 md:translate-x-1/2': rightHalf },
      { 'left-1/2 -translate-x-1/2': middle },
      className
    )}>
      {
        avm && (
          <div className='text-3xl font-bold text-secondary -mt-60 pb-22'>{toPrice(avm)}</div>
        )
      }
      <i className={classNames(
        'block box-content w-8 h-8 border-[4px] border-white border-solid rounded-half bg-font absolute top-1 -translate-y-1/2',
        { 'left-1/2 -translate-x-1/2': middle },
        { 'left-0 md:left-1/2 md:-translate-x-1/2': left || leftHalf },
        { 'right-0 md:right-1/2 md:translate-x-1/2': right || rightHalf },
      )} />
      {
        price && <div className='text-base md:text-xl'>{toPrice(price)}</div>
      }
      {
        name && (<div className='text-neutral-5 text-base'>{name}</div>)
      }
      {children}
    </div>
  )
}

function AgentComment({ option, confidenceScore }) {
  let label = ''
  let tip = ''

  if (!option) {
    switch (confidenceScore) {
      case 'high':
        label = 'High Accuracy'
        tip = 'Our accuracy depends on how much reliable external data we can access. High accuracy meaning we have smallest range of great data'
        break
      case 'low':
        label = 'Low Accuracy'
        tip = 'Our accuracy depends on how much reliable external data we can access. Low accuracy meaning we have widest range of minimal data'
        break
      case 'medium':
        label = 'Medium Accuracy'
        tip = 'Our accuracy depends on how much reliable external data we can access. Medium accuracy meaning we have standard range of standard data'
        break
    }
  } else if ([1, 2].includes(option)) {
    label = 'Agent Comment'
    if (option === 1) {
      tip = 'This property has undergone building works completed since the latest Rateable Valuation (RV). As OneRoof estimates incorporate the RV, these changes may not be accounted for.'
    } else {
      tip = 'This property or title may have been significantly altered since its latest Rateable Valuation (RV). As OneRoof estimates incorporate the RV, these changes may not be accounted for.'
    }
  }

  const { mobile } = useRecoilValue(global)

  return (
    <div className='text-sm flex items-center justify-center gap-x-4 font-extralight'>
      {label}
      {
        tip && (
          <Tooltip dark right={!mobile} bottom={mobile} content={
            <div className='px-16 py-10 text-xs w-240'>{tip}</div>
          }>
            <i className='icon icon-info text-secondary block'></i>
          </Tooltip>
        )
      }
    </div>
  )
}